import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, SearchCheck } from "lucide-react";

const FIELDS = [["shipping_number", "Container number"], ["bill_of_lading", "Bill of lading"], ["booking_reference", "Booking reference"]];

export const TrackingReferences = ({ container, closed, onChanged }) => {
  const [form, setForm] = useState({ shipping_number: container.shipping_number || "", bill_of_lading: container.bill_of_lading || "", booking_reference: container.booking_reference || "" });
  const [saving, setSaving] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const dirty = FIELDS.some(([k]) => (form[k] || "") !== (container[k] || ""));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(([k]) => { if ((form[k] || "") !== (container[k] || "")) payload[k] = form[k]; });
      const r = await api.patch(`/containers/${container.id}`, payload);
      const s = r.data.tracking_sync;
      if (s) toast[s.status === "ERROR" || s.status === "REFERENCE_MISMATCH" ? "error" : "success"](`References saved · carrier check: ${s.status.replace(/_/g, " ").toLowerCase()}${s.carrier_events != null ? ` (${s.carrier_events} events)` : ""}`);
      else toast.success("Shipment references updated — audited");
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const verify = async () => {
    setProbing(true);
    try {
      const r = await api.post("/container-tracking/carrier/probe", { reference: container.shipping_number, container_id: container.id, bill_of_lading: container.bill_of_lading || undefined });
      setProbe(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setProbing(false); }
  };

  return (
    <div className="mt-6 rounded-md border border-border bg-sand/30 p-4" data-testid="tracking-references">
      <div className="flex flex-wrap items-end gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k} className="min-w-[180px] flex-1 space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-warmgrey">{label}</Label>
            <Input value={form[k]} disabled={closed} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value.toUpperCase() }))} placeholder={k === "shipping_number" ? "e.g. JZPU4096884" : "e.g. ZIMUCSB0003479"} className="h-9 bg-white font-mono text-xs" data-testid={`ref-input-${k}`} />
          </div>
        ))}
        {!closed && (
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="h-9 bg-charcoal text-white hover:bg-charcoal/90" data-testid="ref-save">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save references"}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={verify} disabled={probing || !container.shipping_number} className="h-9 border-limestone" data-testid="ref-verify-carrier">
          {probing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><SearchCheck className="mr-1 h-3.5 w-3.5" />Verify with carrier</>}
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-warmgrey">Read-only carrier check: events come from the container number, carrier ETA from the bill of lading; nothing is applied. Several containers may share one B/L or booking.</p>
      <ProbeDialog probe={probe} onClose={() => setProbe(null)} />
    </div>
  );
};

const ProbeDialog = ({ probe, onClose }) => (
  <Dialog open={!!probe} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl" data-testid="carrier-probe-dialog">
      <DialogHeader><DialogTitle className="font-serif text-xl">Carrier verification — {probe?.reference}</DialogTitle></DialogHeader>
      {probe && !probe.ok && <p className="text-sm text-red-700" data-testid="probe-error">{probe.error_kind}: {probe.error}</p>}
      {probe?.verification && (
        <div className={`rounded-md border p-3 text-xs ${probe.verification.ok ? "border-sage/50 bg-sage/10 text-charcoal" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="probe-verification">
          <span className="font-semibold">{probe.verification.ok ? "References match" : "Reference mismatch — Review Required"}</span> · B/L {probe.verification.bl_reference || "—"} · booking {probe.verification.booking_reference || "—"} · containers on B/L: {probe.verification.containers_on_bl.join(", ") || "none"}
          {!probe.verification.ok && <ul className="mt-1 list-disc pl-4">{probe.verification.issues.map((i) => <li key={i}>{i}</li>)}</ul>}
        </div>
      )}
      {probe?.ok && (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto text-xs" data-testid="probe-events">
          <p className="text-warmgrey">Carrier ETA (Miami): <span className="font-medium text-charcoal">{probe.carrier_eta || "not provided for this reference"}</span>{probe.schema?.final_eta ? ` · raw ${probe.schema.final_eta}` : ""}</p>
          {probe.normalized_events.map((e, i) => {
            const t = e.times?.event || e.times?.eta;
            const dry = probe.dry_run?.[i];
            return (
              <div key={i} className="rounded border border-border bg-white p-2" data-testid={`probe-event-${i}`}>
                <span className="font-medium">{e.event_type.replace(/_/g, " ")}</span> · {t?.display || e.event_time}{t ? ` (UTC ${t.utc})` : ""} · {e.location}{e.vessel ? ` · ${e.vessel}${e.voyage ? ` / ${e.voyage}` : ""}` : ""} · <span className="uppercase text-warmgrey">{e.event_kind}</span>
                {dry && <span className={`ml-2 ${dry.duplicate ? "text-warmgrey" : dry.flags?.length ? "text-amber-700" : "text-sage"}`}>{dry.duplicate ? "already recorded" : dry.flags?.length ? `would flag: ${dry.flags.map((f) => f.code).join(", ")}` : dry.stage_change ? "would advance stage" : "would record"}</span>}
              </div>
            );
          })}
          {probe.ignored_events.length > 0 && <p className="text-warmgrey">{probe.ignored_events.length} carrier events ignored (transshipment, customs, release): {probe.ignored_events.map((e) => `${e.eventName} @ ${e.port}`).join(" · ")}</p>}
        </div>
      )}
    </DialogContent>
  </Dialog>
);
